import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Container = styled.div `
  --activeBgColour: ${brand.tertiary.teal};
  --activeFgColour: ${brand.primary.white};
  --bgColour: ${brand.primary.stone};
  --borderRadius: 12px;
  --buttonHeight: 72px;
  --fgColour: ${brand.primary.black};
  --gridGap: 0px;
  --rowItems: 1;
  --shadow-color: 0deg 0% 71%;

  position: relative;

  @media ${until(Device.TabletLarge)} {
    height: var(--buttonHeight);
  }

  @media ${from(Device.TabletLarge)} {
    --gridGap: ${fluid(10, 24, Device.Desktop)};
    --rowItems: 3;
  }

  @media ${from(Device.DesktopSmall)} {
    --rowItems: 4;
  }

  @media ${from(Device.Desktop)} {
    --rowItems: 5;
  }
`;
const Inner = styled(m.ul) `
  display: flex;
  flex-direction: column;
  gap: var(--gridGap);

  @media ${until(Device.TabletLarge)} {
    --borderWidth: 3px;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 0 16px;
    background-color: var(--activeBgColour);
    overflow: hidden;
    z-index: 1;
    border-radius: var(--borderRadius);
    box-shadow: 0px 0.1px 0.1px hsl(var(--shadow-color) / 0.26),
      0px 0.5px 0.6px -0.4px hsl(var(--shadow-color) / 0.26),
      0px 0.9px 1px -0.7px hsl(var(--shadow-color) / 0.26),
      0px 1.5px 1.7px -1.1px hsl(var(--shadow-color) / 0.26),
      0.1px 2.3px 2.6px -1.4px hsl(var(--shadow-color) / 0.26),
      0.1px 3.7px 4.2px -1.8px hsl(var(--shadow-color) / 0.26),
      0.2px 5.5px 6.2px -2.1px hsl(var(--shadow-color) / 0.26),
      0.3px 8.1px 9.1px -2.5px hsl(var(--shadow-color) / 0.26);

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      z-index: -1;
      border-radius: 0 0 var(--borderRadius) var(--borderRadius);
    }

    &::before {
      opacity: 0;
      top: calc(var(--buttonHeight) - var(--borderWidth) * 2);
      left: var(--borderWidth);
      height: calc(100% - var(--buttonHeight) + 3px);
      width: calc(100% - var(--borderWidth) * 2);
      background-color: var(--bgColour);
    }

    &[data-open='true']::before {
      opacity: 1;
    }

    &::after {
      left: var(--borderWidth);
      top: 1px;
      width: calc(100% - var(--borderWidth) * 2);
      height: var(--buttonHeight);
      background-color: var(--activeBgColour);
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    box-shadow: none !important;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }
`;
const Item = styled.li `
  @media ${until(Device.TabletLarge)} {
    &[data-active='true'] {
      order: -1;
    }
  }

  @media ${from(Device.TabletLarge)} {
    flex: 0 0 calc((100% - (var(--gridGap) * (var(--rowItems) - 1))) / var(--rowItems));
  }
`;
const Button = styled.button `
  ${ButtonReset};
  ${transition('background-color, color', 0.25)};

  display: flex;
  align-items: center;
  gap: ${fluid(12, 16, Device.Desktop)};
  min-height: var(--buttonHeight);
  height: 100%;
  padding: 12px 0;
  width: 100%;

  &:hover,
  &:focus-visible,
  ${Item}[data-active='true'] & {
    background-color: var(--activeBgColour);
    color: var(--activeFgColour);
  }

  @media ${until(Device.TabletLarge)} {
    flex: 0 0 var(--buttonHeight);
    width: 100%;

    ${Item}[data-active='false'] & {
      border-top: 1px solid var(--activeBgColour);
    }
  }

  @media ${from(Device.TabletLarge)} {
    padding-left: 12px;
    padding-right: 10px;
    background-color: var(--bgColour);
    border-radius: var(--borderRadius);
    color: var(--fgColour);
  }
`;
const Icon = styled.span `
  display: grid;
  place-items: center;
  width: 48px;
  aspect-ratio: 1;
  color: ${brand.primary.black};
  background-color: ${brand.primary.white};
  border-radius: 8px;

  svg {
    display: block;
    height: 24px;
    width: 24px;
  }
`;
const Text = styled.span `
  ${fonts.poppins.regular};

  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${fluid(0, 4)};
  line-height: 1.25;
`;
const Label = styled.span `
  font-weight: 800;
  font-size: ${fluid(16, 18, Device.Desktop)};
`;
const SubLabel = styled.span `
  font-size: ${fluid(10, 12)};
`;
const Chevron = styled.span `
  ${transition('opacity')};

  display: grid;
  place-items: center;
  width: 32px;
  aspect-ratio: 1;
  color: ${brand.primary.black};
  background-color: ${brand.primary.white};
  border-radius: 8px;
  margin: auto 0 auto auto;
  opacity: 0;
  flex: 0 0 32px;

  ${Item}[data-hide-dot='false'] & {
    margin-left: 0;
  }

  svg {
    ${transition('transform')};

    display: block;
    width: 14px;

    ${Container}[data-open='true'] & {
      transform: rotate(180deg);
    }
  }

  ${Item}[data-active='true'] & {
    opacity: 1;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const Dot = styled.span `
  ${transition('opacity')};

  width: 8px;
  aspect-ratio: 1;
  background-color: ${brand.primary.white};
  margin: auto 0 auto auto;
  flex: 0 0 8px;
  opacity: 0;
  border-radius: 5000px;

  ${Item}[data-active='true'] & {
    opacity: 1;
  }

  @media ${until(Device.TabletLarge)} {
    ${Item}[data-hide-dot='true'] & {
      display: none;
    }
  }
`;
const FilterTabButtonsStyles = {
    Button,
    Chevron,
    Container,
    Icon,
    Inner,
    Item,
    Label,
    SubLabel,
    Text,
    Dot,
};
export default FilterTabButtonsStyles;
